import React from 'react';
import { Button, Flex, Text } from '@radix-ui/themes';
import { ReloadIcon, RocketIcon } from '@radix-ui/react-icons';
import { useVersionCheck } from '../hooks/useVersionCheck';

/**
 * App-wide banner that appears when a newer frontend build has been deployed
 * than the one this tab is running. Mounted once at the app root so it guards
 * the entire UI against stale JS (see useVersionCheck for the rationale).
 *
 * We surface a banner with an explicit Reload rather than force-reloading, so a
 * clinician mid-charting never loses in-progress work to a silent refresh.
 */
const VersionUpdateBanner: React.FC = () => {
  const { updateAvailable, reload } = useVersionCheck();

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'var(--blue-9)',
        color: 'white',
        boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
      }}
    >
      <Flex align="center" justify="center" gap="3" px="4" py="2" wrap="wrap">
        <RocketIcon />
        <Text size="2" weight="medium" style={{ color: 'white' }}>
          A new version of Phanova CCM is available.
        </Text>
        <Button size="1" variant="surface" color="gray" highContrast onClick={reload}>
          <ReloadIcon />
          Reload
        </Button>
      </Flex>
    </div>
  );
};

export default VersionUpdateBanner;
